import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Database, Server, Mail, CreditCard, MessageSquare, Activity,
  Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
} from "lucide-react";

const ICONS: Record<string, any> = {
  db: Database, queue: Server, sendgrid: Mail, stripe: CreditCard, whatsapp: MessageSquare,
};
const LABELS: Record<string, string> = {
  db: "Banco de Dados", queue: "Fila (pg-boss)", sendgrid: "SendGrid",
  stripe: "Stripe (Cobrança)", whatsapp: "WhatsApp Cloud API",
};

/** Mesma paleta do statusVisual, para os níveis do vigia de saturação. */
function satVisual(level: string) {
  if (level === "saturado") return { color: "text-red-600", bg: "bg-red-500/10", label: "Saturado" };
  if (level === "degradado") return { color: "text-amber-600", bg: "bg-amber-500/10", label: "Degradado" };
  // "Sem leitura" é cinza, e não vermelho, de propósito: não conseguir medir
  // não é o mesmo que banco ruim — pintar de vermelho foi o que fez o alerta
  // de 22/09/2026 mandar todo mundo caçar uma saturação que não existia.
  if (level === "desconhecido") return { color: "text-muted-foreground", bg: "bg-muted", label: "Sem leitura" };
  return { color: "text-emerald-600", bg: "bg-emerald-500/10", label: "Normal" };
}

function statusVisual(status: string) {
  if (status === "up")       return { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", label: "OK" };
  if (status === "degraded") return { icon: AlertTriangle, color: "text-amber-600",   bg: "bg-amber-500/10",   label: "Degradado" };
  if (status === "down")     return { icon: XCircle,       color: "text-red-600",     bg: "bg-red-500/10",     label: "Fora do ar" };
  return                          { icon: HelpCircle,     color: "text-muted-foreground", bg: "bg-muted",       label: "Desconhecido" };
}

export function HealthTab() {
  const { data, isLoading, refetch, isFetching } = trpc.superAdmin.platformHealth.useQuery(
    undefined,
    { refetchInterval: 30_000 }, // 30s
  );

  if (isLoading || !data) {
    return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  const services: { key: keyof typeof ICONS; data: any }[] = [
    { key: "db", data: data.db },
    { key: "queue", data: data.queue },
    { key: "sendgrid", data: data.sendgrid },
    { key: "stripe", data: data.stripe },
    { key: "whatsapp", data: data.whatsapp },
  ];

  const fmtUptime = (sec: number) => {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h`;
    return `${Math.round(sec / 86400)}d`;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            Saúde da plataforma
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Pings em tempo real para todas as integrações críticas. Atualiza a cada 30s.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Re-verificar agora
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(({ key, data: svc }) => {
          const Icon = ICONS[key];
          const v = statusVisual(svc.status);
          const VIcon = v.icon;
          return (
            <Card key={key}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${v.bg}`}><Icon className={`w-4 h-4 ${v.color}`} /></div>
                    <div>
                      <p className="font-semibold text-sm">{LABELS[key]}</p>
                      <p className="text-[11px] text-muted-foreground">{svc.detail ?? "OK"}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`${v.bg} ${v.color} border-0 text-[10px]`}>
                      <VIcon className="w-3 h-3 mr-1" /> {v.label}
                    </Badge>
                    {svc.latencyMs != null && (
                      <span className="text-[10px] text-muted-foreground">{svc.latencyMs}ms</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Saturação do banco — o sinal que faltava nos apagões de agosto/2026,
          quando a única pista chegava com o site já fora do ar. Vem da última
          amostra do vigia (server/_core/db-saturation.ts), não de uma consulta
          nova: banco sob pressão não precisa de mais uma query na fila. */}
      {data.dbSaturation && (() => {
        const sat = data.dbSaturation;
        const v = satVisual(sat.level);
        // Sem leitura não há número: mostrar "0/0 (100%)" seria inventar.
        const medido = sat.maxConnections > 0;
        return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Carga do banco
              <Badge className={`${v.bg} ${v.color} border-0 text-[10px]`}>{v.label}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Conexões</p>
                <p className="font-semibold mt-0.5">
                  {medido ? (
                    <>
                      {sat.connections}/{sat.maxConnections}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        ({Math.round(sat.connectionRatio * 100)}%)
                      </span>
                    </>
                  ) : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Queries ativas</p>
                <p className="font-semibold mt-0.5">{medido ? sat.activeQueries : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Query mais longa</p>
                <p className="font-semibold mt-0.5">{medido ? `${Math.round(sat.longestQuerySec)}s` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Latência</p>
                {/* Sem leitura, o que há aqui é o tempo até o vigia desistir —
                    chamar isso de latência do banco foi o erro do alerta. */}
                <p className="font-semibold mt-0.5">{medido ? `${sat.latencyMs}ms` : "—"}</p>
              </div>
            </div>
            {sat.reasons.length > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                {sat.reasons.join(" · ")}
              </p>
            )}
          </CardContent>
        </Card>
        );
      })()}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Processo</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Uptime</p>
              <p className="font-semibold mt-0.5">{fmtUptime(data.uptimeSec)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Node</p>
              <p className="font-semibold mt-0.5">{data.nodeVersion}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Heap</p>
              <p className="font-semibold mt-0.5">{data.memoryMB.heapUsed} / {data.memoryMB.heapTotal} MB</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">RSS</p>
              <p className="font-semibold mt-0.5">{data.memoryMB.rss} MB</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
